import { Component, ElementRef, Input, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { environment } from 'src/environments/environment';
import { LazyLoadImageModule } from 'ng-lazyload-image';

import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { TraitCountPipe } from '@/pipes/trait-count.pipe';
import { PlatformService } from '@/services/platform.service';

import { DataService } from '@/services/data.service';
import { PunkCardService } from '@/services/punk-card.service';

import { Attribute, Punk } from '@/models/graph';
import { TOKENWORKS_ADDRESS, PUNKAUCTION_ADDRESS } from '@/constants/utils';
import { MarketplaceIconsComponent } from '@/components/marketplace-icons/marketplace-icons.component';

import { firstValueFrom, map, tap } from 'rxjs';

@Component({
  selector: 'app-punk-billboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LazyLoadImageModule,
    RouterModule,

    TokenIdParsePipe,
    MarketplaceIconsComponent
  ],
  templateUrl: './punk-billboard.component.html',
  styleUrls: ['./punk-billboard.component.scss']
})

export class PunkBillboardComponent {

  @Input() data!: Punk | null;

  protected readonly environment = environment;
  protected readonly TOKENWORKS_ADDRESS = TOKENWORKS_ADDRESS;
  protected readonly PUNKAUCTION_ADDRESS = PUNKAUCTION_ADDRESS;

  @ViewChild('pfp') pfp!: ElementRef;
  @ViewChild('tokenImage') tokenImage!: ElementRef;

  backgroundColorPicker = new FormControl<string | null>('#638596', []);
  borderColorPicker = new FormControl<string | null>('#638596', []);

  selectedBackgroundColor: string | null = '#638596';
  selectedBorderColor: string | null = null;

  pfpModalOpen!: boolean;

  shapeCheck = new FormControl('square');
  border: boolean = false;
  resolution: string = 'pfp';

  width: number = 480;
  height: number = 480;
  scale: number = 2;

  ctx!: CanvasRenderingContext2D | null;

  constructor(
    private http: HttpClient,
    public location: Location,
    public dataSvc: DataService,
    private tokenIdParsePipe: TokenIdParsePipe,
    private punnkCardSvc: PunkCardService,
    private traitCountPipe: TraitCountPipe,
    private platformService: PlatformService,
  ) {
    this.ctx?.scale(this.scale, this.scale);

    this.shapeCheck.valueChanges.pipe(
      tap((res) => this.paintCanvas())
    ).subscribe();

    this.backgroundColorPicker.valueChanges.subscribe((res) => {
      this.selectedBackgroundColor = res;
    });

    this.borderColorPicker.valueChanges.subscribe((res) => {
      this.selectedBorderColor = res;
    });
    

  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // CANVAS PFP STUFFS /////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  openPFPModal(): void {
    this.pfpModalOpen = true;
    this.paintCanvas();
  }

  changeBorder() {
    this.border = !this.border;
    this.paintCanvas();
  }

  async paintCanvas(): Promise<void> {
    if (!this.data) return;

    const shape = this.shapeCheck.value;

    const canvas = this.pfp.nativeElement as HTMLCanvasElement;

    canvas.width = this.width;
    canvas.height = this.height;

    canvas.style.width = this.width / this.scale + 'px';
    canvas.style.height = this.height / this.scale + 'px';

    this.ctx?.restore();
    this.ctx?.clearRect(0, 0, this.width, this.height);
    this.ctx = canvas.getContext('2d');
    if (environment.collection === 'cryptophunks') {
      this.ctx?.transform(-1, 0, 0, 1, this.width, 0);
    }    
    if (!this.ctx) return;

    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.imageSmoothingEnabled = false;

    this.ctx.fillStyle = (this.selectedBackgroundColor || '#638596');

    if (shape === 'square') {
      this.ctx.fillRect(0, 0, this.width, this.height);

      if (this.border) {
        this.ctx.lineWidth = this.width / 10;
        this.ctx.strokeStyle = (this.selectedBorderColor || '#638596');
        this.ctx.strokeRect(0, 0, this.width, this.height);
      }
    }

    if (shape === 'round') {
      this.ctx.beginPath();
      this.ctx.arc(
        this.width / 2,
        this.height / 2,
        this.width / 2,
        0,
        this.scale * Math.PI
      );
      this.ctx.fill();
      this.ctx.save();
      this.ctx.clip();

      if (this.border) {
        this.ctx.lineWidth = this.width / 10;
        this.ctx.strokeStyle = (this.selectedBorderColor || '#638596');
        this.ctx.strokeStyle = (this.selectedBorderColor || '#638596');
        this.ctx.stroke();
      }
    }

    if (shape === 'hex') {
      this.ctx.beginPath();

      const path = new Path2D();
      const svgElement = this.platformService.createElementNS("http://www.w3.org/2000/svg", "svg") as any;
      const m = svgElement?.createSVGMatrix();
      const hex = new Path2D('M231.902238,88.7456104 C223.144463,69.1521217 212.932259,50.3330983 201.361627,32.4672826 L197.629553,26.7602925 C193.034661,19.6679209 187.01294,13.7534619 180.0012,9.44704633 C172.98946,5.14057966 165.162903,2.55018067 157.092742,1.86407789 L150.540611,1.302316 C130.212204,-0.434105333 109.786956,-0.434105333 89.4592692,1.302316 L82.9071381,1.86407789 C74.8364967,2.55018067 67.0109002,5.14057966 59.99892,9.44704633 C52.9870597,13.7534619 46.9649793,19.6679209 42.3703274,26.7602925 L38.6382528,32.5183519 C27.0678614,50.3841675 16.8552971,69.2031909 8.09762995,88.7966797 L5.27757355,95.1037337 C1.80283206,102.882731 0,111.387296 0,120 C0,128.612831 1.80283206,137.11714 5.27757355,144.896265 L8.09762995,151.203319 C16.8552971,170.797318 27.0678614,189.616342 38.6382528,207.481647 L42.3703274,213.239706 C46.9649793,220.33195 52.9870597,226.247047 59.99892,230.553463 C67.0109002,234.859879 74.8364967,237.450367 82.9071381,238.135972 L89.4592692,238.697734 C109.786956,240.434089 130.212204,240.434089 150.540611,238.697734 L157.092742,238.135972 C165.168903,237.44143 172.99906,234.839451 180.0108,230.518991 C187.02374,226.198532 193.041861,220.26939 197.629553,213.163102 L201.361627,207.405043 C212.932259,189.539738 223.144463,170.720715 231.902238,151.126715 L234.722294,144.819661 C238.196364,137.040536 240,128.536227 240,119.923395 C240,111.310692 238.196364,102.806128 234.722294,95.0271298 L231.902238,88.7456104 Z')

      const t = m.scale(2);
      path.addPath(hex, t);
      this.ctx.fill(path);
      this.ctx.save();
      this.ctx.clip(path);

      if (this.border) {
        this.ctx.lineWidth = this.width / 10;
        this.ctx.strokeStyle = (this.selectedBorderColor || '#638596');
        this.ctx.strokeStyle = (this.selectedBorderColor || '#638596');
        this.ctx.stroke(path);
      }
    }

    if (shape === 'trans') {
      this.ctx.clearRect(0, 0, this.width, this.height);
    }

    await this.drawPunk();
  }

  async drawPunk(): Promise<void> {
    const svg = await this.getPunkImage();
    if (!svg) return;

    return new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.ctx?.drawImage(
          img,
          0,
          0,
          this.width,
          this.height,
        );
        resolve();
      };
      img.onerror = err => {
        reject(err);
      };
      const dataUrl = 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
      img.src = dataUrl;
    });
  }

  async getPunkImage(): Promise<string | undefined> {
    if (!this.data) return;
    const imgUrl = environment.staticDownloadUrl + this.tokenIdParsePipe.transform(this.data.id) + '.svg';
    const svg = await firstValueFrom(
      this.http.get(imgUrl, { responseType: 'text' }).pipe(
        // tap((res) => console.log(res)),
        map((svgStr) => {
          // Parse the SVG string into a DOM object
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgStr, 'image/svg+xml');
          const svgElem = svgDoc.documentElement;

          // Add width and height attributes. Using the viewBox values
          const viewBox = svgElem.getAttribute('viewBox')?.split(' ').map(Number);
          if (!viewBox) return;
          svgElem.setAttribute('width', viewBox[2].toString());
          svgElem.setAttribute('height', viewBox[3].toString());

          // Serialize the modified SVG DOM object back to a string
          const serializer = new XMLSerializer();
          return serializer.serializeToString(svgElem);
        })
      )
    );
    return svg;
  }

  /** V1 accent: purple when wrapped, green when native (matches billboard PFP). */
  private v1AccentColor(format: 'hex' | 'rgba'): string {
    const wrapped = !!this.data?.wrapped;
    if (format === 'rgba') {
      return wrapped ? 'rgba(166, 154, 254, 1)' : 'rgba(101, 165, 111, 1)';
    }
    return wrapped ? '#A698FE' : '#65a56f';
  }

  /** V1 download-card logo badge: green when unwrapped, default purple SVG when wrapped. */
  private v1CardLogoSvg(svg: string): string {
    if (this.data?.wrapped) {
      return svg;
    }
    return svg.replace(/#a69afe/gi, this.v1AccentColor('hex'));
  }

  async createCard(
    punkSVGData: string,
    attributes: Attribute[],
    punkId: string,
    type?: 'sales' | 'bids' | 'offers'
  ): Promise<string> {

    const canvasWidth = 1200;
    const canvasHeight = 1200;

    const punkWidth = canvasWidth / 2;
    const punkHeight = canvasHeight / 2;

    const bleed = 30 * 2;

    const lowerThird = ((canvasHeight / 3) * 2) - (bleed * 2) + 20;

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const ctx = canvas.getContext('2d');

    if (!ctx) throw new Error('Could not create canvas context.');

    ctx.fillStyle = '#131415';

    if (environment.collection === 'cryptophunks') {
      ctx.fillStyle = '#490b3b';
    } else if (environment.collection === 'cryptopunks-v1') {
      // ctx.fillStyle = '#35345b';
      ctx.fillStyle = '#333333';
      //--punk-default-color: 101, 165, 111;
    }

    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    const color = environment.collection === 'cryptopunks-v1'
      ? this.v1AccentColor('rgba')
      : 'rgba(99, 133, 150, 1)';

    const font = (size: number) => `normal ${size}px RetroComputer`;

    ctx.fillStyle = color;
    ctx.fillRect(bleed, bleed, canvasWidth - (bleed * 2), lowerThird);

    const dataUrl = 'data:image/svg+xml;utf8,' + encodeURIComponent(punkSVGData);

    await new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {

        ctx.save()
        if (environment.collection === 'cryptophunks') {
          ctx.transform(-1, 0, 0, 1, this.width, 0);
        }

        const pos = (canvasWidth / 2) - (punkWidth / 2) - (bleed / 2)

        ctx.drawImage(
          img,
          environment.collection === 'cryptophunks' ? pos - punkWidth - 100 : pos,
          lowerThird - punkHeight + bleed,
          punkWidth,
          punkHeight,
        );

        ctx.restore()
        resolve();
      }
      img.onerror = err => {
        reject(err);
      };
      img.width = punkWidth / 10;
      img.height = punkHeight / 10;
      img.src = dataUrl;
    });

    // Line 1 (left side)
    const line1 = environment.collection === 'cryptophunks' ? 'CryptoPhunk' : environment.collection === 'cryptopunks-v1' ? 'CryptoPunk V1' : 'CryptoPunk';
    const line1Pos = lowerThird + (bleed * 2);
    ctx.textBaseline = 'top';
    ctx.font = font(36);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(
      line1,
      bleed,
      line1Pos
    );

    // Line 2 (left side)
    ctx.textBaseline = 'top';
    ctx.font = font(120);
    ctx.fillStyle = environment.collection === 'cryptophunks' ? '#fc00c2' : color || '#FF04B4';
    ctx.fillText(
      punkId,
      bleed - 5,
      line1Pos + 50
    );

    // Line 3 (right side)
    
    const sex = attributes[0];
    
    const line3Pos = lowerThird + (bleed * 2);
    const punkLabel = environment.collection === 'cryptophunks' ? 'phunks' : 'punks';
    const line3_1 = `${sex.v} ${punkLabel}`;
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(
      line3_1,
      canvasWidth - bleed,
      line3Pos
    );

    const line3_1Width = (ctx.measureText(line3_1).width + 8);
    const traitCount = this.traitCountPipe.transform(attributes[0].v);

    const line3_2 = `${traitCount}`;
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = environment.collection === 'cryptophunks' ? '#fc00c2' : color || '#FF04B4';
    ctx.fillText(
      line3_2,
      canvasWidth - bleed - line3_1Width,
      line3Pos
    );

    const line3_2Width = (ctx.measureText(line3_2).width + 8);

    const line3_3 = `One of`;
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(
      line3_3,
      canvasWidth - bleed - line3_1Width - line3_2Width,
      line3Pos
    );

    const line4Pos = lowerThird + (bleed * 2) + 40;
    const line4_1 = `Trait${attributes.length > 2 ? 's' : ''}`
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(
      line4_1,
      canvasWidth - bleed,
      line4Pos
    );

    const line4_1Width = (ctx.measureText(line4_1).width + 8);

    const line4_2 = `${attributes.length - 2}`;
    ctx.font = font(24);
    ctx.textAlign = 'right';
    ctx.fillStyle = environment.collection === 'cryptophunks' ? '#fc00c2' : color || '#FF04B4';
    ctx.fillText(
      line4_2,
      canvasWidth - bleed - line4_1Width,
      line4Pos
    );

    let traitsPos = line4Pos + 30;
    for (const attr of attributes) {

      if (attr.k !== 'Skintone' && attr.k !== 'Sex') {

        const val = this.traitCountPipe.transform(attr.v);
        const lineTrait_1 = `${(+val * 100) / 10000}%`;
        ctx.font = font(22);
        ctx.textAlign = 'right';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(
          lineTrait_1,
          canvasWidth - bleed,
          traitsPos
        );

        const lineTrait_1Width = (ctx.measureText(lineTrait_1).width + 8);

        const lineTrait_2 = `${attr.v}`;
        ctx.font = font(22);
        ctx.textAlign = 'right';
        ctx.fillStyle = environment.collection === 'cryptophunks' ? '#fc00c2' : color || '#FF04B4';
        ctx.fillText(
          lineTrait_2,
          canvasWidth - bleed - lineTrait_1Width,
          traitsPos
        ); 
      } 

      traitsPos = traitsPos + 30;
    }    

    // const imgUrl = '/assets/logo.svg';
    const imgUrl = environment.collection === 'cryptophunks' ? '/assets/cryptophunks-dark.svg' : 
                   environment.collection === 'cryptopunks-v1' ? '/assets/cryptopunksv1.svg' :
                   this.dataSvc.staticUrl + '/icons/logo.svg';
    let logo = await firstValueFrom(this.http.get(imgUrl, { responseType: 'text' }));
    if (environment.collection === 'cryptopunks-v1') {
      logo = this.v1CardLogoSvg(logo);
    }

    await new Promise<void>((resolve, reject) => {
      const logoImg = new Image();
      logoImg.onload = () => {
        let y = canvasHeight - bleed - 20;
        if (environment.collection === 'cryptophunks') {
          y -= 30
        } 
        ctx.drawImage(logoImg, bleed, y);
        resolve();
      }
      logoImg.onerror = err => {
        reject(err);
      };
      logoImg.src = 'data:image/svg+xml;utf8,' + encodeURIComponent(logo);
    });

    const base64 = canvas.toDataURL('image/png;base64');
    return base64;
  }

  async downloadCard(): Promise<void> {

    const canvas = this.pfp.nativeElement as HTMLCanvasElement;

    canvas.width = this.width;
    canvas.height = this.height;

    canvas.style.width = this.width / this.scale + 'px';
    canvas.style.height = this.height / this.scale + 'px';

    this.ctx?.restore();
    this.ctx?.clearRect(0, 0, this.width, this.height);
    this.ctx = canvas.getContext('2d');

    if (!this.data) return;
    if (!this.ctx) return;

    const svg = await this.getPunkImage();
    if (!svg) return;

    const cardImage = await this.createCard(
      svg,
      this.data.attributes,
      this.data.id,
    );

    this.ctx.clearRect(0, 0, this.width, this.height);

    await new Promise<void>((resolve, reject) => {
      const graphicImg = new Image();
      graphicImg.onload = () => {
        this.ctx?.drawImage(
          graphicImg,
          0,
          0,
          this.width,
          this.height,
        );
        resolve();
      };
      graphicImg.onerror = err => {
        reject(err);
      };
      graphicImg.src = cardImage;
    });

    // console.log({ cardImage })
    this.downloadImage(cardImage);
  }

  downloadImage(cardImage: string): void {
    const link = this.platformService.createElement('a') as HTMLAnchorElement;
    if (link) {
      link.download = environment.collection + '-' + this.data?.id + '.png';
      link.target = '_blank';
      link.href = cardImage;
      link.click();
    }
  }

  downloadCanvas(): void {
    const link = this.platformService.createElement('a') as HTMLAnchorElement;
    if (link) {
      link.download = environment.collection + '-' + this.data?.id + '.png';
      link.target = '_blank';
      link.href = this.pfp.nativeElement.toDataURL('image/png;base64');
      link.click();
      this.pfpModalOpen = false;
    }
  }

  share(el: HTMLButtonElement): void {
    if (!this.data) return;

    const buttonText = el.innerText;
    const buttonWidth = el.offsetWidth;

    el.innerText = 'Link Copied!';
    el.style.width = buttonWidth + 'px';
    el.style.textAlign = 'center';

    this.platformService.setTimeout(() => {
      el.innerText = buttonText;
      el.style.width = 'auto';
    }, 2000);

    const param = Date.now();
    this.dataSvc.fetchPunkCard(this.data.id, param);
    const urlToShare = `${environment.siteUrl}/${this.data.id}?=${param}`;
    navigator.clipboard.writeText(urlToShare);
  }


  ngOnChanges(changes: SimpleChanges) {
    if (environment.collection === 'cryptopunks-v1') {
      if (changes['data'] && changes['data'].currentValue !== changes['data'].previousValue) {
        const accent = this.v1AccentColor('hex');
        this.selectedBackgroundColor = accent;
        this.selectedBorderColor = accent;
        this.borderColorPicker.setValue(accent);
        this.backgroundColorPicker.setValue(accent);
      }
    } else if (environment.collection === 'cryptopunks') {
      /*
      if (this.data?.c721wrapped) {
        this.selectedBackgroundColor = '#66A570'
        this.selectedBorderColor = '#66A570'
        this.borderColorPicker.setValue('#66A570')
        this.backgroundColorPicker.setValue('#66A570')
      } else {
        this.selectedBackgroundColor = '#638596'
      }
        */
    }
  }
}
